import { useState } from "react";
import { CHANNEL_LABEL, STATE_LABEL } from "./labels";

// published and killed are off-board: the board is for work in hand. Killing
// needs a reason, so it stays in the drawer — there is no killed column and
// therefore no drop handler that could ever be bound with that state.
const BOARD_STATES = ["idea", "drafting", "review", "queued"];

export default function QueueBoard({ items, onOpen, onMove }) {
  const [dragging, setDragging] = useState(null);   // the item being dragged
  const [over, setOver] = useState(null);           // the column under the cursor

  const start = (item) => (e) => {
    setDragging(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  const allow = (state) => (e) => {
    // A column only accepts a card the state machine would accept.
    if (dragging && (dragging.legal_transitions || []).includes(state)) {
      e.preventDefault();
      setOver(state);
    }
  };

  const drop = (state) => async (e) => {
    e.preventDefault();
    setOver(null);
    const item = dragging;
    setDragging(null);
    if (!item || item.state === state) return;
    // No local try/catch: `onMove` is Queue.move, which reports a failed
    // transition into the queue's own banner above this board — with the
    // reason the server gave, which a message composed here could not carry.
    // A second catch here would either double the banner or, as it did
    // before, replace that reason with a generic sentence.
    await onMove(item.id, state);
  };

  // Native HTML5 drag-and-drop is mouse-only. Cards are also click- and
  // keyboard-openable (Enter/Space, matching PlaybookCard's header and the
  // list rows), and the drawer's own transition buttons — driven by the
  // same legal_transitions array — give a keyboard user a complete path to
  // move an item without a keyboard drag system.
  const openKey = (id) => (e) => {
    if (e.key === "Enter") {
      onOpen(id);
    } else if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onOpen(id);
    }
  };

  return (
    <div data-testid="mk-board">
      <div className="mk-board">
        {BOARD_STATES.map((state) => {
          const rows = items.filter((i) => i.state === state);
          const droppable = dragging && (dragging.legal_transitions || []).includes(state);
          return (
            <div
              key={state}
              className={`mk-col ${over === state ? "over" : ""} ${droppable ? "droppable" : ""}`}
              onDragOver={allow(state)}
              onDragLeave={() => setOver(null)}
              onDrop={drop(state)}
              data-testid={`mk-col-${state}`}
            >
              <div className="mk-col-head">
                {STATE_LABEL[state]} <span className="mk-count">{rows.length}</span>
              </div>
              {rows.map((it) => (
                <div
                  key={it.id}
                  className="mk-cardlet"
                  draggable
                  onDragStart={start(it)}
                  onDragEnd={() => { setDragging(null); setOver(null); }}
                  onClick={() => onOpen(it.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={openKey(it.id)}
                  data-testid={`mk-cardlet-${it.id}`}
                >
                  <div className="mk-cardlet-title">{it.title}</div>
                  <div className="mk-cardlet-meta">
                    {it.athlete_name && <span className="mk-tag">{it.athlete_name}</span>}
                    {(it.channels || []).slice(0, 2).map((c) => (
                      <span key={c} className="mk-tag">{CHANNEL_LABEL[c]}</span>
                    ))}
                    <span className={`mk-pill author-${it.author}`}>{it.author}</span>
                  </div>
                </div>
              ))}
              {!rows.length && <div className="mk-col-empty">—</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
